import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';
import { TIMELINE_HEIGHT } from '../constants';
import { CherryPopover } from './CherryPopover';
import { getContentStyle } from './getContentStyle';
import { getDragStyles } from './getDragStyle';
import TimeLine from './Timeline';

const CherryPickGrid = ({ toolbarPosition, data, videoFile, onVideoClick, bottomOffset, currentView, filterStore }) => {
    const theme = useTheme();
    const [timelineLength, setTimelineLength] = useState(0);
    const [infoAnchorEl, setInfoAnchorEl] = useState(null);
    const [infoRow, setInfoRow] = useState(null);
    const dragStyles = getDragStyles(theme);
    const timelineRef = useRef(null);

    const SENTIMENT_COLORS = {
        positive: theme.palette.success.main,
        negative: theme.palette.error.main,
        neutral: theme.palette.grey[500],
        default: theme.palette.grey[400]
    };

    const handleDragStart = (e, row, index) => {
        const dragData = { id: row.id, row, index, thumbnail: row.thumbnail, sentence: row.sentence };
        e.dataTransfer.setData("text/plain", JSON.stringify(dragData));
        const dragImage = new Image();
        dragImage.src = row.thumbnail;
        if (dragImage.complete) e.dataTransfer.setDragImage(dragImage, 0, 0);
        e.currentTarget.style.opacity = dragStyles.dragging.opacity;
        e.currentTarget.style.transform = dragStyles.dragging.transform;
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '';
        e.currentTarget.style.transform = '';
    };

    const handleDragHandleDoubleClick = (row, index) => {
        const clipData = { id: row.id, row, index, thumbnail: row.thumbnail, sentence: row.sentence };
        if (timelineRef.current) {
            timelineRef.current.addClip(clipData);
            if (filterStore && currentView) filterStore.setSelectedSentences(row.id, currentView);
            const element = document.getElementById(`video-cell-${row.id || index}`);
            if (element) {
                element.style.transition = 'background-color 0.3s';
                element.style.backgroundColor = theme.palette.action.selected;
                setTimeout(() => { element.style.backgroundColor = ''; }, 300);
            }
        }
    };

    const handleInfoOpen = (event, row) => {
        event.preventDefault();
        if (!infoAnchorEl) { setInfoAnchorEl(event.currentTarget); setInfoRow(row); }
    };
    const handleInfoClose = (event) => {
        event.preventDefault();
        if (infoAnchorEl) { setInfoAnchorEl(null); setInfoRow(null); }
    };

    const sentenceRows = data;

    return (
        <Box sx={{
            ...getContentStyle(toolbarPosition),
            width: '100%',
            height: `calc(100vh - ${TIMELINE_HEIGHT}px)`,
            overflowY: 'auto',
            p: 2,
            pb: `${TIMELINE_HEIGHT + 32}px`,
        }}
            onMouseMove={(e) => infoAnchorEl && handleInfoClose(e)}
        >
            {sentenceRows.length > 0 && videoFile ? (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(auto-fill, minmax(${Math.max(filterStore.getVideoWidth(currentView), 200)}px, 1fr))`,
                    gridAutoRows: 'auto',
                    width: '100%',
                    gap: 2,
                    alignContent: 'start',
                }}>
                    {sentenceRows.map((row, index) => {
                        const borderColor = SENTIMENT_COLORS[row.sentiment] || SENTIMENT_COLORS.default;
                        return (
                            <Box
                                id={`video-cell-${row.id || index}`}
                                key={row.id || index}
                                sx={{
                                    display: 'flex', flexDirection: 'column',
                                    border: `2px solid ${borderColor}`,
                                    p: 1, borderRadius: 1,
                                    minHeight: '200px', maxHeight: '400px',
                                    position: 'relative',
                                    transition: 'transform 0.2s, opacity 0.2s, background-color 0.3s',
                                    '&:hover': { boxShadow: 3 },
                                    ...dragStyles.videoCell,
                                }}
                                draggable={true}
                                onDragStart={(e) => handleDragStart(e, row, index)}
                                onDragEnd={handleDragEnd}
                            >
                                <Box className="info-button" sx={{
                                    position: 'absolute', top: 5, left: 5,
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: '4px', display: 'none',
                                    alignItems: 'center', justifyContent: 'center',
                                    p: 0.5, zIndex: 2, boxShadow: 1,
                                    ...dragStyles.infoButton,
                                }} onClick={(e) => handleInfoOpen(e, row)}>
                                    <InfoIcon fontSize="small" />
                                </Box>

                                <Box className="drag-handle" sx={{
                                    position: 'absolute', top: 5, right: 5,
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: '4px', display: 'none',
                                    alignItems: 'center', justifyContent: 'center',
                                    p: 0.5, zIndex: 2, boxShadow: 1,
                                    ...dragStyles.dragHandle,
                                }} onDoubleClick={() => handleDragHandleDoubleClick(row, index)}>
                                    <DragIndicatorIcon fontSize="small" />
                                </Box>

                                <video
                                    id={`v-${index}`}
                                    playsInline
                                    style={{ width: '100%', height: 'auto', marginBottom: '8px', cursor: 'pointer', borderRadius: '4px' }}
                                    src={videoFile}
                                    preload="none"
                                    poster={row.thumbnail}
                                    onClick={(event) => onVideoClick(event, row, index)}
                                />
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                    {row.sentence}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                <Typography variant="body1">
                    {videoFile ? 'No sentences to display.' : 'Video file not available.'}
                </Typography>
            )}

            <CherryPopover
                infoAnchorEl={infoAnchorEl}
                handleInfoClose={handleInfoClose}
                infoRow={infoRow}
                theme={theme}
                sentimentColors={SENTIMENT_COLORS}
            />

            <TimeLine
                ref={timelineRef}
                toolbarPosition={toolbarPosition}
                bottomOffset={bottomOffset}
                filterStore={filterStore}
                currentView={currentView}
                onItemsChange={(items) => setTimelineLength(items.length)}
            />
        </Box>
    );
};

export default CherryPickGrid;
